<div class="admin-plans-page account-settings-page">
  @if (isInitialLoading) {
    <section class="page-card card">
      <div class="card-body py-5 text-center">
        <div class="spinner-border text-primary" role="status"></div>
        <p class="mt-3 mb-0">Loading plan catalog...</p>
      </div>
    </section>
  } @else if (hasInitialLoadError) {
    <app-api-load-error
      title="Could not load billing plan config"
      message="Some error occurred while loading the global plan catalog."
      retryLabel="Retry"
      (retry)="loadPlans()"
    ></app-api-load-error>
  } @else if (selectedPlan && draft) {
    <div class="d-grid gap-4">
      @if (saveMessage) {
        <div class="alert alert-success mb-0">{{ saveMessage }}</div>
      }
      @if (saveError) {
        <div class="alert alert-danger mb-0">{{ saveError }}</div>
      }

      <section class="page-card card">
        <div class="card-body d-flex flex-column flex-xl-row justify-content-between align-items-xl-center gap-3">
          <div>
            <h3 class="section-title mb-0 bw-title-with-info">
              Plans & billing config
              <app-info-tooltip compact placement="right" text="Edit the global plan catalog. The API keeps enabled self-serve plans synchronized with Paddle."></app-info-tooltip>
            </h3>
          </div>

          <div class="d-flex flex-wrap gap-2 align-items-center">
            <span class="badge text-bg-secondary">{{ plans.length }} plans</span>
            <span class="badge text-bg-primary">Selected: {{ selectedPlan.code }}</span>
          </div>
        </div>
      </section>

      <div class="admin-plans-layout">
        <aside class="admin-plans-list page-card card">
          <div class="card-body d-grid gap-3">
            <div>
              <h3 class="section-title mb-0 bw-title-with-info">
                Catalog
                <app-info-tooltip compact placement="right" text="The custom plan remains code-defined and hidden from self-serve until it is enabled again."></app-info-tooltip>
              </h3>
            </div>

            <div class="admin-plans-list__items">
              @for (plan of plans; track plan.code) {
                <button
                  type="button"
                  class="admin-plans-list__item"
                  [class.is-selected]="selectedPlanCode === plan.code"
                  (click)="selectPlan(plan.code)"
                >
                  <div class="admin-plans-list__item-top">
                    <strong>{{ plan.name }}</strong>
                    <span class="badge text-bg-secondary text-uppercase">{{ plan.code }}</span>
                  </div>
                  <div class="admin-plans-list__item-meta">
                    <span>{{ plan.priceEur === 0 ? 'Free' : (plan.priceEur + ' EUR / ' + plan.billingInterval) }}</span>
                    @if (plan.selfServeVisible) {
                      <span class="badge text-bg-primary-subtle">Visible</span>
                    } @else {
                      <span class="badge text-bg-secondary">Hidden</span>
                    }
                    @if (plan.paddleBillingEnabled) {
                      <span class="badge text-bg-success">Paddle</span>
                    }
                  </div>
                </button>
              }
            </div>
          </div>
        </aside>

        <section class="page-card card">
          <div class="card-body d-grid gap-4">
            <div class="admin-plans-editor__header">
              <div>
                <h3 class="section-title mb-0 bw-title-with-info">
                  {{ selectedPlan.name }}
                  <app-info-tooltip compact placement="right" [text]="'Internal code ' + selectedPlan.code + ' is immutable. Other plan settings can be adjusted below.'"></app-info-tooltip>
                </h3>
              </div>

              <div class="admin-plans-editor__header-badges">
                <span class="badge text-bg-secondary">{{ selectedPlanPriceLabel }}</span>
                @if (selectedPlan.paddleBillingEnabled) {
                  <span class="badge text-bg-success">Paddle managed</span>
                }
                @if (!selectedPlan.selfServeVisible) {
                  <span class="badge text-bg-warning">Hidden from self-serve</span>
                }
              </div>
            </div>

            <form class="d-grid gap-4" (ngSubmit)="savePlan()">
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label">Plan name</label>
                  <input class="form-control" type="text" [(ngModel)]="draft.name" name="plan_name" required />
                </div>
                <div class="col-md-3">
                  <label class="form-label">Monthly price (EUR)</label>
                  <input class="form-control" type="number" min="0" [(ngModel)]="draft.priceEur" name="plan_price" required />
                </div>
                <div class="col-md-3">
                  <label class="form-label">Sort order</label>
                  <input class="form-control" type="number" min="0" [(ngModel)]="draft.sortOrder" name="plan_sort_order" required />
                </div>
              </div>

              <div>
                <label class="form-label">Description</label>
                <textarea class="form-control" rows="3" [(ngModel)]="draft.description" name="plan_description"></textarea>
              </div>

              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label">Max active websites</label>
                  <input class="form-control" type="number" min="1" [(ngModel)]="draft.maxActiveWebsites" name="plan_max_websites" />
                  <div class="form-text">Leave empty for unlimited.</div>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Max team members</label>
                  <input class="form-control" type="number" min="1" [(ngModel)]="draft.maxTeamMembers" name="plan_max_team_members" />
                  <div class="form-text">Leave empty for unlimited.</div>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Included AI tokens / month</label>
                  <input class="form-control" type="number" min="0" step="1000" [(ngModel)]="draft.assistantIncludedTokens" name="plan_assistant_included_tokens" required />
                  <div class="form-text">Set to 0 to disable included BizzWeaver AI usage for this plan.</div>
                </div>
              </div>

              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label">Template access level</label>
                  <input class="form-control" type="text" [(ngModel)]="draft.templateAccessLevel" name="plan_template_access" required />
                </div>
                <div class="col-md-6">
                  <label class="form-label">Support level</label>
                  <input class="form-control" type="text" [(ngModel)]="draft.supportLevel" name="plan_support_level" required />
                </div>
              </div>

              <div class="admin-plans-switch-grid">
                <label class="admin-plans-switch">
                  <input type="checkbox" [(ngModel)]="draft.allowCustomDomain" name="plan_allow_custom_domain" />
                  <span>
                    <strong>Custom domains</strong>
                    <small>Allow custom domain connections on this plan.</small>
                  </span>
                </label>

                <label class="admin-plans-switch">
                  <input type="checkbox" [(ngModel)]="draft.allowExport" name="plan_allow_export" />
                  <span>
                    <strong>Export access</strong>
                    <small>Allow export/download actions for this plan.</small>
                  </span>
                </label>

                <label class="admin-plans-switch">
                  <input type="checkbox" [(ngModel)]="draft.allowPublish" name="plan_allow_publish" />
                  <span>
                    <strong>Publishing</strong>
                    <small>Allow live publishing without forcing an upgrade.</small>
                  </span>
                </label>

                <label class="admin-plans-switch">
                  <input type="checkbox" [(ngModel)]="draft.selfServeVisible" name="plan_self_serve_visible" />
                  <span>
                    <strong>Visible to customers</strong>
                    <small>Show this plan in the logged-in `/billing/plans` page.</small>
                  </span>
                </label>

                <label class="admin-plans-switch">
                  <input
                    type="checkbox"
                    [(ngModel)]="draft.paddleBillingEnabled"
                    name="plan_paddle_enabled"
                    [disabled]="selectedPlan.code === 'free'"
                  />
                  <span>
                    <strong>Paddle-managed</strong>
                    <small>Sync this plan into Paddle self-serve catalog automatically.</small>
                  </span>
                </label>
              </div>

              <div class="admin-plans-ids">
                <article class="payment-card">
                  <span class="section-kicker">Paddle product ID</span>
                  <strong>{{ selectedPlan.paddleProductId || 'Not synced yet' }}</strong>
                </article>
                <article class="payment-card">
                  <span class="section-kicker">Paddle price ID</span>
                  <strong>{{ selectedPlan.paddlePriceId || 'Not synced yet' }}</strong>
                </article>
              </div>

              <div class="d-flex flex-wrap gap-2 justify-content-end">
                <button type="button" class="btn btn-outline-secondary" (click)="resetDraft()">Reset changes</button>
                <button type="submit" class="btn btn-primary" [disabled]="isSaving">
                  @if (!isSaving) {
                    <span>Save plan config</span>
                  } @else {
                    <span>Saving...</span>
                  }
                </button>
              </div>
            </form>
          </div>
        </section>
      </div>

      <section class="page-card card admin-credit-packs">
        <div class="card-body d-grid gap-4">
          <div class="admin-plans-editor__header">
            <div>
              <h3 class="section-title mb-0 bw-title-with-info">
                Prepaid AI token packs
                <app-info-tooltip compact placement="right" text="Customers purchase these one-time Paddle packs before using them. Tokens are granted only after Paddle confirms the completed payment. Launch pricing is based on the configured GPT-5 model's input, cached-input, and output rates, with a payment-fee and operating margin buffer."></app-info-tooltip>
              </h3>
            </div>
            <div class="d-flex flex-wrap gap-2 justify-content-end">
              <span class="badge text-bg-secondary">{{ creditPacks.length }} packs</span>
              <button type="button" class="btn btn-outline-primary btn-sm" (click)="createRecommendedCreditPacks()" [disabled]="isCreditPacksLoading || isCreatingRecommendedCreditPacks || isCreditPackSaving">
                {{ isCreatingRecommendedCreditPacks ? 'Creating...' : 'Add recommended packs' }}
              </button>
            </div>
          </div>

          @if (creditPackMessage) {
            <div class="alert alert-success mb-0">{{ creditPackMessage }}</div>
          }
          @if (creditPackError) {
            <div class="alert alert-danger mb-0">{{ creditPackError }}</div>
          }

          <div class="admin-credit-packs__layout">
            <aside class="admin-credit-packs__list">
              <button type="button" class="btn btn-outline-primary w-100" (click)="newCreditPack()">New token pack</button>
              <p class="admin-credit-packs__recommendation mb-0">Recommended launch pricing: 100k for EUR 2, 500k for EUR 8.50, and 1m for EUR 15. These are BizzWeaver AI tokens across input, cached input, output, and reasoning usage, not OpenAI credits.</p>
              @if (isCreditPacksLoading) {
                <p class="mb-0 text-secondary small">Loading AI credit packs...</p>
              } @else if (creditPacks.length === 0) {
                <p class="mb-0 text-secondary small">Create the first pack, review its price, then activate it for customers.</p>
              } @else {
                @for (pack of creditPacks; track pack.publicId) {
                  <button type="button" class="admin-credit-packs__item" [class.is-selected]="selectedCreditPackId === pack.publicId" (click)="selectCreditPack(pack.publicId)">
                    <strong>{{ pack.label }}</strong>
                    <span>{{ pack.tokenAmount | number }} tokens · {{ pack.priceCents / 100 | currency:'EUR':'symbol':'1.2-2' }}</span>
                    <small>{{ pack.isActive ? 'Available to customers' : 'Draft / inactive' }}</small>
                  </button>
                }
              }
            </aside>

            <form class="d-grid gap-3" (ngSubmit)="saveCreditPack()">
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label">Pack label</label>
                  <input class="form-control" type="text" [(ngModel)]="creditPackDraft.label" name="credit_pack_label" minlength="2" maxlength="120" required />
                </div>
                <div class="col-md-3">
                  <label class="form-label">Token amount</label>
                  <input class="form-control" type="number" [(ngModel)]="creditPackDraft.tokenAmount" name="credit_pack_tokens" min="1000" step="1000" required />
                </div>
                <div class="col-md-3">
                  <label class="form-label">Price (EUR)</label>
                  <input class="form-control" type="number" [(ngModel)]="creditPackDraft.priceEur" name="credit_pack_price" min="0.5" step="0.01" required />
                </div>
              </div>

              <div>
                <label class="form-label">Customer description</label>
                <textarea class="form-control" rows="2" [(ngModel)]="creditPackDraft.description" name="credit_pack_description" maxlength="500"></textarea>
              </div>

              <div class="row g-3 align-items-end">
                <div class="col-md-4">
                  <label class="form-label">Sort order</label>
                  <input class="form-control" type="number" [(ngModel)]="creditPackDraft.sortOrder" name="credit_pack_sort" min="0" step="1" required />
                </div>
                <div class="col-md-8">
                  <label class="admin-plans-switch">
                    <input type="checkbox" [(ngModel)]="creditPackDraft.isActive" name="credit_pack_active" />
                    <span>
                      <strong>Available to customers</strong>
                      <small>Inactive packs are retained for audit history but cannot be purchased.</small>
                    </span>
                  </label>
                </div>
              </div>

              @if (selectedCreditPack; as selectedCreditPack) {
                <div class="admin-plans-ids">
                  <article class="payment-card">
                    <span class="section-kicker">Paddle product ID</span>
                    <strong>{{ selectedCreditPack.paddleProductId || 'Created when first offered' }}</strong>
                  </article>
                  <article class="payment-card">
                    <span class="section-kicker">Paddle price ID</span>
                    <strong>{{ selectedCreditPack.paddlePriceId || 'Created when first offered' }}</strong>
                  </article>
                </div>
              }

              <div class="d-flex flex-wrap gap-2 justify-content-end">
                <button type="button" class="btn btn-outline-secondary" (click)="selectedCreditPack ? selectCreditPack(selectedCreditPack.publicId) : newCreditPack()">Reset changes</button>
                <button type="submit" class="btn btn-primary" [disabled]="isCreditPackSaving">
                  {{ isCreditPackSaving ? 'Saving...' : (selectedCreditPack ? 'Save token pack' : 'Create token pack') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  } @else {
    <section class="page-card card">
      <div class="card-body">
        <h3 class="section-title bw-title-with-info">
          No plans available
          <app-info-tooltip compact placement="right" text="The global plan catalog did not return editable plans. Check the API response and seed data."></app-info-tooltip>
        </h3>
      </div>
    </section>
  }
</div>
